import { DateTime } from 'luxon';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getStartSessionTimestamp, Session } from '@mindly/ui-components';
import { ConsultationType } from 'src/pages/types';
import { merge } from 'lodash';

type NewConsultationsListState = {
  futureSessions: Record<string, ConsultationType | Session>;
  futureSessionsLoaded: boolean;
  previousSessions: Record<string, ConsultationType | Session>;
  previousSessionsLoaded: boolean;
  previousSessionsTotal: number;
};

const initialState: NewConsultationsListState = {
  futureSessions: {},
  futureSessionsLoaded: false,
  previousSessions: {},
  previousSessionsLoaded: false,
  previousSessionsTotal: 0,
};

export const newConsultationsListSlice = createSlice({
  name: 'newConsultationsList',
  initialState,
  reducers: {
    setFutureSessions: (state, action: PayloadAction<Array<ConsultationType | Session>>) => {
      const futureSessions = action.payload?.filter(
        (session) =>
          getStartSessionTimestamp(session.consultation_time) >= DateTime.local().minus({ minutes: 50 }).toMillis()
      );
      state.futureSessions = futureSessions.reduce(
        (acc, session) => {
          acc[session.id] = session;
          return acc;
        },
        {} as Record<string, ConsultationType | Session>
      );
      state.futureSessionsLoaded = true;
    },
    addNewFutureSession: (state, action: PayloadAction<ConsultationType | Session>) => {
      const session = action.payload;
      if (getStartSessionTimestamp(session.consultation_time) >= DateTime.local().minus({ minutes: 50 }).toMillis()) {
        state.futureSessions[session.id] = session;
      }
    },
    updateFutureSession: (state, action: PayloadAction<{ id: string; session: ConsultationType | Session }>) => {
      const { id, session } = action.payload;

      if (!state.futureSessions[id]) {
        return;
      }

      state.futureSessions = {
        ...state.futureSessions,
        [id]: merge(state.futureSessions[id], session),
      };
    },
    removeFutureSession: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const futureSessions = Object.assign({}, state.futureSessions);
      delete futureSessions[id];
      state.futureSessions = futureSessions;
    },
    setPreviousSessions: (state, action: PayloadAction<Array<ConsultationType | Session>>) => {
      const previousSessions = action.payload?.filter(
        (session) =>
          getStartSessionTimestamp(session.consultation_time) < DateTime.local().minus({ minutes: 50 }).toMillis()
      );
      if (previousSessions?.length) {
        state.previousSessions = previousSessions.reduce(
          (acc, session) => {
            acc[session.id] = session;
            return acc;
          },
          {} as Record<string, ConsultationType | Session>
        );
      }
      state.previousSessionsLoaded = true;
    },
    updatePreviousSession: (state, action: PayloadAction<{ id: string; session: ConsultationType | Session }>) => {
      const { id, session } = action.payload;

      if (!state.previousSessions[id]) {
        return;
      }

      state.previousSessions = {
        ...state.previousSessions,
        [id]: merge(state.previousSessions[id], session),
      };
    },
    removePreviousSession: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const previousSessions = Object.assign({}, state.previousSessions);
      delete previousSessions[id];
      state.previousSessions = previousSessions;
    },
    setPreviousSessionsTotal: (state, action: PayloadAction<number>) => {
      state.previousSessionsTotal = action.payload;
    },
    updateSessionsTime: (state) => {
      try {
        const consultations: (ConsultationType | Session)[] = [];
        [...Object.values(state.futureSessions ?? {}), ...Object.values(state.previousSessions ?? {})].forEach(
          (session) => {
            if (!consultations.some((c) => c.id === session.id)) {
              consultations.push(session);
            }
          }
        );

        state.futureSessions = consultations
          .filter(
            (session) =>
              getStartSessionTimestamp(session.consultation_time) >= DateTime.local().minus({ minutes: 50 }).toMillis()
          )
          .reduce(
            (acc, session) => {
              acc[session.id] = session;
              return acc;
            },
            {} as Record<string, ConsultationType | Session>
          );

        state.previousSessions = consultations
          .filter(
            (session) =>
              getStartSessionTimestamp(session.consultation_time) < DateTime.local().minus({ minutes: 50 }).toMillis()
          )
          .reduce(
            (acc, session) => {
              acc[session.id] = session;
              return acc;
            },
            {} as Record<string, ConsultationType | Session>
          );
      } catch (error) {
        console.error(error);
      }
    },
  },
});

export const {
  setFutureSessions,
  addNewFutureSession,
  updateFutureSession,
  removeFutureSession,
  setPreviousSessions,
  updatePreviousSession,
  removePreviousSession,
  setPreviousSessionsTotal,
  updateSessionsTime,
} = newConsultationsListSlice.actions;
export default newConsultationsListSlice.reducer;
